import { useMemo } from "react";
import { UIOrder, UIUnifiedPriceBreakdownItem } from "@flights/types";
import { useI18n, t } from "@bookingcom/lingojs-react";
import { isFeatureRunningClientSide, localizationUtils, ryanairUtils } from "@flights/web-api-utils-universal";
import { transformDisclaimersForPriceBreakdown } from "./transforms/disclaimers";
import uniq from "lodash/uniq";
import { BreakdownProps } from "./Breakdown/Breakdown";
import useRouteName from "hooks/useRouteName";
import useEvents from "hooks/useEvents";

export type UnifiedPriceBreakdownPostBookingControllerProps = {
  order?: UIOrder;
};

export function useUnifiedPriceBreakdownPostBookingController(props: UnifiedPriceBreakdownPostBookingControllerProps) {
  const { order } = props;
  const i18n = useI18n();
  const routeName = useRouteName();
  const { squeak } = useEvents();
  const segments = useMemo(() => order?.airOrder?.flightSegments, [order?.airOrder?.flightSegments]);
  const instalmentsFees = order?.totalPrice?.instalmentsFees;

  const unifiedPriceBreakdown = useMemo(() => {
    const upb = order?.unifiedPriceBreakdown;
    if (isFeatureRunningClientSide("FLIGHTS_PRICING_ENABLE_PRODUCT_ID_SQUEAK") && upb && !upb.id) {
      squeak(
        `UnifiedPriceTag::useUnifiedPriceBreakdownPostBookingController - product id is missing - route "${routeName}"`
      );
    }

    return upb;
  }, [order?.unifiedPriceBreakdown, routeName, squeak]);

  const isHungaryDepartingRyanAir = useMemo(
    () => localizationUtils.isHungaryDepartingRyanAirFlight(segments),
    [segments]
  );

  const addedItems: UIUnifiedPriceBreakdownItem[] = useMemo(() => {
    if (!instalmentsFees) return [];
    const installmentItem: UIUnifiedPriceBreakdownItem = {
      id: "dynamic-instalments-fees",
      scope: "FEE",
      title: i18n.trans(t("flights_price_breakdown_instalment_label")),
      price: instalmentsFees,
      items: []
    };
    return [installmentItem];
  }, [i18n, instalmentsFees]);

  const disclaimers = useMemo(() => {
    return uniq(transformDisclaimersForPriceBreakdown(order?.totalPrice));
  }, [order]);

  const isRyanair = useMemo(() => ryanairUtils.isAnyRyanairSegments(segments), [segments]);

  const expandedByDefault = useMemo<BreakdownProps["expandedByDefault"]>(() => {
    return isRyanair
      ? {
          FLIGHT_ADULT: true,
          FLIGHT_CHILD: true,
          FLIGHT_INFANT: true,
          EXTRA: true
        }
      : undefined;
  }, [isRyanair]);

  return {
    unifiedPriceBreakdown,
    isHungaryDepartingRyanAir,
    expandedByDefault,
    disclaimers,
    addedItems
  };
}
